import { message } from 'antd';
import {
  makeDateId,
  buildDateLabel,
  replaceInFilterArray,
  removeFromFilterArray,
  toLabel,
} from '@/pages/Shipments/Filters/utils/labels';

interface FilterItem {
  filterBy: string;
  filterValue: string[];
}

interface AppliedFilter {
  id: string;
  name?: string;
  dateFilter?: string;
  filterBy?: string;
  start?: string;
  end?: string;
  [key: string]: unknown;
}

interface DateRangeValue {
  oldId?: string;
  dateFilter: string;
  rangeStart: string;
  rangeEnd: string;
  period?: string;
  newId?: string;
}

type BuildPayload = (
  filterArray: FilterItem[],
  appliedFilters: AppliedFilter[]
) => unknown | null;

interface Params {
  appliedFilters: AppliedFilter[];
  filterArray: FilterItem[];
  updateFilterArrayState: (params: {
    filterBy: string;
    filterValue: string[];
  }) => FilterItem[];
  setAppliedFilters: (filters: AppliedFilter[]) => void;
  setFilterArray: (filterArray: FilterItem[]) => void;
  setShowAppliedFilters: (show: boolean) => void;
  setFilteredData?: (data: unknown[]) => void;
  setSecondFilterDisplay: (show: boolean) => void;
  buildPayload: BuildPayload;
  setFiltersPayload: (payload: unknown) => void;
  handleFiltersToParent?: (
    filterArray: FilterItem[],
    filters: AppliedFilter[]
  ) => void;
}

export default function useDateRangeApplyPost({
  appliedFilters,
  filterArray,
  updateFilterArrayState,
  setAppliedFilters,
  setFilterArray,
  setShowAppliedFilters,
  setFilteredData,
  setSecondFilterDisplay,
  buildPayload,
  setFiltersPayload,
  handleFiltersToParent,
}: Params) {
  const applyDateRange = (value: DateRangeValue | null | undefined): void => {
    if (!value) return;

    const {
      oldId,
      dateFilter,
      rangeStart,
      rangeEnd,
      period,
      newId: incomingNewId,
    } = value;
    const key = dateFilter;

    const newId = incomingNewId || makeDateId(rangeStart, rangeEnd);
    const labelPrefix = toLabel(key);

    const name =
      period && period !== 'Custom'
        ? `${labelPrefix}: ${period}`
        : buildDateLabel(key, rangeStart, rangeEnd);

    const nextApplied = Array.isArray(appliedFilters)
      ? [...appliedFilters]
      : [];

    const dupIdx = nextApplied.findIndex(
      (it) =>
        (it.dateFilter === key || it.filterBy === key) &&
        String(it.id) === String(newId)
    );

    if (oldId && String(oldId) === String(newId)) {
      message.info('Those dates are already applied.');
      setSecondFilterDisplay(false);
      return;
    }

    if (oldId) {
      const oldIdx = nextApplied.findIndex(
        (it) =>
          (it.dateFilter === key || it.filterBy === key) &&
          String(it.id) === String(oldId)
      );

      if (dupIdx !== -1 && dupIdx !== oldIdx) {
        if (oldIdx !== -1) nextApplied.splice(oldIdx, 1);
        const cleanedFA = removeFromFilterArray(
          filterArray,
          key,
          String(oldId)
        );
        setFilterArray(cleanedFA);
        setAppliedFilters(nextApplied);
        setShowAppliedFilters(nextApplied.length > 0);
        setFilteredData?.([]);
        setSecondFilterDisplay(false);
        if (handleFiltersToParent) {
          handleFiltersToParent(cleanedFA, nextApplied);
        } else {
          setFiltersPayload(buildPayload(cleanedFA, nextApplied));
        }

        message.info('Those dates are already applied.');
        return;
      }

      if (oldIdx !== -1) {
        nextApplied[oldIdx] = {
          ...nextApplied[oldIdx],
          id: newId,
          name,
          dateFilter: key,
          start: rangeStart,
          end: rangeEnd,
          period, // when change form period to custom or vice versa
        };
      } else {
        nextApplied.push({
          id: newId,
          name,
          dateFilter: key,
          start: rangeStart,
          end: rangeEnd,
          period, // when change form period to custom or vice versa
        });
      }

      const nextFA = replaceInFilterArray(
        filterArray,
        key,
        String(oldId),
        String(newId)
      );
      setFilterArray(nextFA);
      setAppliedFilters(nextApplied);
      setShowAppliedFilters(nextApplied.length > 0);
      setFilteredData?.([]);
      setSecondFilterDisplay(false);
      if (handleFiltersToParent) {
        handleFiltersToParent(nextFA, nextApplied);
      } else {
        setFiltersPayload(buildPayload(nextFA, nextApplied));
      }

      return;
    }

    if (dupIdx !== -1) {
      message.info('Those dates are already applied.');
      setSecondFilterDisplay(false);
      return;
    }

    nextApplied.push({
      id: newId,
      name,
      dateFilter: key,
      start: rangeStart,
      end: rangeEnd,
      ...(period ? { period } : {}),
    });

    const nextFA = updateFilterArrayState({
      filterBy: key,
      filterValue: [newId],
    });
    setAppliedFilters(nextApplied);
    setShowAppliedFilters(nextApplied.length > 0);
    setFilteredData?.([]);
    setSecondFilterDisplay(false);
    if (handleFiltersToParent) {
      handleFiltersToParent(nextFA, nextApplied);
    } else {
      setFiltersPayload(buildPayload(nextFA, nextApplied));
    }
  };

  return { applyDateRange };
}
